import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { Modal } from "../../../ui/modal/modal.tsx";
import { TextField } from "../../../ui/text-field/text-field.tsx";
import { Button } from "../../../ui/button/button.tsx";
import { Select } from "../../../ui/select/select.tsx";
import { GET_ONBOARDING_CONTACTS, GET_ONBOARDING_EMPLOYEE_LIST } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi, postFormDataMultipart } from "../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { useUser } from "../../../../lib/rbac/useUser.ts";

import styles from "./invite-pre-join-modal.module.css";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  employeeName: z.string().min(1, "Employee name is required"),
  onboardingFor: z.enum(["INDIA", "US"], {
    message: "Onboarding region is required",
  }),
  pointOfContactId: z.string().min(1, "Point of Contact is required"),
});

const ONBOARDING_REGION_OPTIONS = [
  { value: "INDIA", label: "India" },
  { value: "US", label: "US" },
];

export type InvitePreJoinModalProps = {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
};

type OnboardingContact = {
  id: number;
  fullName: string;
};

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export function InvitePreJoinModal({ open, onClose, onInvited }: InvitePreJoinModalProps) {
  const user = useUser();
  const [email, setEmail] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [onboardingFor, setOnboardingFor] = useState("");
  const [pointOfContactId, setPointOfContactId] = useState("");
  const [contactOptions, setContactOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setEmployeeName("");
      setOnboardingFor("");
      setPointOfContactId("");
      setContactOptions([]);
      setErrors({});
      setBusy(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    setContactsLoading(true);
    void getApi<OnboardingContact[]>(GET_ONBOARDING_CONTACTS, accessToken(), "json", controller.signal)
      .then(({ data }) => {
        if (!active) return;
        const contacts = Array.isArray(data) ? data : [];
        setContactOptions(contacts.map((contact) => ({ value: String(contact.id), label: contact.fullName })));
        const loggedInContact = contacts.find((contact) => contact.id === user?.id);
        setPointOfContactId(loggedInContact ? String(loggedInContact.id) : "");
      })
      .catch((error: unknown) => {
        if (!active || (error instanceof DOMException && error.name === "AbortError")) return;
        setContactOptions([]);
        setPointOfContactId("");
        showError(error instanceof Error ? error.message : "Unable to load onboarding contacts.");
      })
      .finally(() => { if (active) setContactsLoading(false); });

    return () => {
      active = false;
      controller.abort();
    };
  }, [open, user?.id]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrors({});
      const parsed = schema.safeParse({ email, employeeName, onboardingFor, pointOfContactId });
      if (!parsed.success) {
        const next: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const key = issue.path[0];
          if (typeof key === "string" && next[key] == null) {
            next[key] = issue.message;
          }
        }
        setErrors(next);
        return;
      }
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("emailId", parsed.data.email);
        fd.append("employeeName", parsed.data.employeeName);
        fd.append("onboardingFor", parsed.data.onboardingFor);
        fd.append("contactEmpId", parsed.data.pointOfContactId);
        const { data } = await postFormDataMultipart<{ message?: string }>(
          GET_ONBOARDING_EMPLOYEE_LIST,
          accessToken(),
          fd,
        );
        const msg =
          data != null && typeof data === "object" && "message" in data && typeof data.message === "string"
            ? data.message
            : "Invitation sent.";
        showSuccess(msg);
        onInvited();
        onClose();
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : "Something went wrong. Please try again.";
        showError(msg);
      } finally {
        setBusy(false);
      }
    },
    [email, employeeName, onboardingFor, pointOfContactId, onClose, onInvited],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send Pre-Join Invitation"
      size="md"
      footer={
        <div className={styles.footer}>
          <Button type="button" variant="light-grey" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="invite-pre-join-form" variant="accent" disabled={busy}>
            {busy ? "Submitting…" : "Submit"}
          </Button>
        </div>
      }
    >
      <form
        id="invite-pre-join-form"
        className={styles.form}
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
      >
        <TextField
          id="invite-pre-email"
          label="Personal Email ID"
          type="email"
          required
          autoComplete="email"
          maxLength={100}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors((current) => ({ ...current, email: "" }));
          }}
          placeholder="Enter Personal Email Id"
          error={errors.email}
          disabled={busy}
        />
        <TextField
          id="invite-pre-name"
          label="Employee Name"
          type="text"
          required
          value={employeeName}
          onChange={(e) => {
            setEmployeeName(e.target.value);
            setErrors((current) => ({ ...current, employeeName: "" }));
          }}
          placeholder="Enter Employee Name"
          error={errors.employeeName}
          disabled={busy}
        />
        <Select
          id="invite-pre-onboarding-for"
          label="Region"
          required
          value={onboardingFor}
          onChange={(value) => {
            setOnboardingFor(value);
            setErrors((current) => ({ ...current, onboardingFor: "" }));
          }}
          options={ONBOARDING_REGION_OPTIONS}
          placeholder="Select Region"
          error={errors.onboardingFor}
          disabled={busy}
        />
        <Select
          id="invite-pre-point-of-contact"
          label="Point of Contact"
          required
          searchable
          value={pointOfContactId}
          onChange={(value) => {
            setPointOfContactId(value);
            setErrors((current) => ({ ...current, pointOfContactId: "" }));
          }}
          options={contactOptions}
          placeholder={contactsLoading ? "Loading Contacts..." : "Select Point of Contact"}
          error={errors.pointOfContactId}
          disabled={busy || contactsLoading}
        />
      </form>
    </Modal>
  );
}
